/**
 * Axios interceptors for handling authentication errors and activity tracking.
 */
import { AXIOS_INSTANCE } from "./queryClient";

const RETURN_URL_KEY = "auth_return_url";
const SESSION_EXPIRED_KEY = "session_expired";
const USER_ACTIVITY_EVENT = "user-activity";

let isRedirecting = false;
let activityInterceptorId: number | null = null;

/**
 * Set up the 401 response interceptor.
 * When a 401 is received, stores the current URL and redirects to login.
 */
export function setupAuthInterceptor(clearAuthState: () => void): void {
  AXIOS_INSTANCE.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only handle 401 errors, and prevent infinite redirect loops
      if (error.response?.status === 401 && !isRedirecting) {
        // Skip redirect for auth check endpoints - these are expected to fail when not logged in
        const requestUrl = error.config?.url || "";
        if (requestUrl.includes("/auth/profile")) {
          return Promise.reject(error);
        }

        isRedirecting = true;

        // Store current location for return after login (skip if already on login)
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== "/login") {
          sessionStorage.setItem(RETURN_URL_KEY, currentPath);
          sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
        }

        // Clear auth state
        clearAuthState();

        // Redirect to login
        window.location.href = "/login";
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Check if the session expired flag is set.
 */
export function checkSessionExpired(): boolean {
  const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY);
  if (expired) {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return true;
  }
  return false;
}

/**
 * Get the stored return URL and clear it.
 */
export function getAndClearReturnUrl(): string {
  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
  if (returnUrl) {
    sessionStorage.removeItem(RETURN_URL_KEY);
    return returnUrl;
  }
  return "/dashboard";
}

/**
 * Reset the redirect flag (call after navigation completes).
 */
export function resetRedirectFlag(): void {
  isRedirecting = false;
}

/**
 * Set up the activity tracking interceptor.
 * Dispatches a custom event on every API request to track user activity.
 */
export function setupActivityInterceptor(): void {
  if (activityInterceptorId !== null) {
    return; // Already set up
  }

  activityInterceptorId = AXIOS_INSTANCE.interceptors.request.use(
    (config) => {
      // Dispatch activity event for inactivity tracking
      window.dispatchEvent(new CustomEvent(USER_ACTIVITY_EVENT));
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Remove the activity tracking interceptor.
 */
export function removeActivityInterceptor(): void {
  if (activityInterceptorId !== null) {
    AXIOS_INSTANCE.interceptors.request.eject(activityInterceptorId);
    activityInterceptorId = null;
  }
}
